import Swiper from 'swiper';
import { FreeMode, Thumbs } from 'swiper/modules';
import { all } from '../lib/dom';

/**
 * Turns `.is-style-carousel` gallery blocks into a Swiper with a thumbnail strip.
 *
 * The block editor emits a plain `<ul><li>` gallery; Swiper wants
 * `.swiper > .swiper-wrapper > .swiper-slide`, so the classes are swapped here and
 * the resulting markup is cloned to build the thumbnails.
 *
 * Swiper stays a dependency on purpose: touch momentum, thumb syncing, keyboard
 * and ARIA handling are a lot of hard-won behaviour to reimplement badly. It is
 * actively maintained and ships its own types. Only the modules used are imported,
 * so the rest of the library tree-shakes away.
 */
export function gallery(): void {
  for (const el of all<HTMLElement>('.is-style-carousel')) {
    const media = el.closest<HTMLElement>('.media');
    // Without the wrapper there is nowhere to put the thumbnails, and Swiper
    // would be initialising against a detached node.
    if (!media) continue;

    toSwiperMarkup(el);

    const thumbsEl = document.createElement('div');
    thumbsEl.className = 'thumbs swiper';
    thumbsEl.innerHTML = el.innerHTML;
    media.append(thumbsEl);

    const thumbs = new Swiper(thumbsEl, {
      modules: [FreeMode],
      spaceBetween: 10,
      slidesPerView: 3,
      // Click a thumb to jump to it; the threshold stops a small drag registering
      // as a click.
      slideToClickedSlide: true,
      threshold: 5,
      watchSlidesProgress: true,
      freeMode: { enabled: true },
    });

    new Swiper(el, {
      modules: [Thumbs],
      spaceBetween: 10,
      thumbs: { swiper: thumbs },
    });
  }
}

/** Rewrite WordPress gallery classes into the structure Swiper expects. */
function toSwiperMarkup(el: HTMLElement): void {
  el.classList.add('swiper');

  const list = el.querySelector('ul');
  if (!list) return;

  list.classList.remove('blocks-gallery-grid');
  list.classList.add('swiper-wrapper');

  for (const item of Array.from(list.querySelectorAll('li'))) {
    item.classList.remove('blocks-gallery-item');
    item.classList.add('swiper-slide');
  }
}
